import { getSubpaths, samplePath } from "../SvgEditor/helpers/splitPathToRails";
import { svgToBase64Png } from "../../lib/firebase/stitchAnalysis";
import { materializeVideoRails } from "./astraSatinClient";
import { SATIN_API, CLOUD_SATIN } from "../../config/stitchServices";
import { cloudStitchJob } from "../../lib/cloudStitchJob";

export async function prepareDesign(
  svg,
  colors,
  settings,
  analysis,
  size,
  fabric
) {
  if (!svg?.children?.length || svg.children.length > 24)
    throw new Error("The design beta supports 1–24 layers.");
  const sourceImage = await svgToBase64Png(svg);
  const layers = [];
  for (let index = 0; index < svg.children.length; index++) {
    const node = svg.children[index],
      layer = settings[index],
      pass = layer?.passes?.[layer.activePassIndex || 0];
    let contours = [],
      issue = "";
    try {
      if (
        node.name !== "path" ||
        node.attributes?.transform ||
        svg.attributes?.transform
      )
        throw new Error("Grouped or transformed geometry needs preparation.");
      const paths = getSubpaths(node.attributes?.d);
      if (!paths.length || paths.length > 128)
        throw new Error("Satin needs 1–128 closed contours in this layer.");
      contours = paths.map((d) => {
        const pts = samplePath(d, 600),
          a = pts[0],
          b = pts[pts.length - 1];
        const extent = Math.max(
          ...pts.map((p) => Math.hypot(p.x - a.x, p.y - a.y))
        );
        if (
          !/z\s*$/i.test(d) &&
          Math.hypot(a.x - b.x, a.y - b.y) > Math.max(1e-6, extent * 1e-5)
        )
          throw new Error("This layer contains open paths.");
        return pts.slice(0, -1);
      });
    } catch (e) {
      issue = e.message;
      contours = [];
    }
    const thumbnail = await svgToBase64Png(
      { ...svg, children: [node] },
      { width: 512, height: 512 }
    );
    layers.push({
      index,
      color: colors[index] || node.attributes?.fill || "#777777",
      currentType: pass?.stitchType || "auto_fill",
      satinSupported: !issue,
      issue,
      contours,
      thumbnail,
      optimizer:
        analysis.layers?.find((l) => l.index === index) ||
        analysis.layers?.[index] ||
        null,
    });
  }
  return {
    sourceImage,
    layers,
    widthMm: size.width,
    heightMm: size.height,
    fabric,
  };
}
export async function requestDesignAgent(
  endpoint,
  payload,
  signal,
  onProgress
) {
  let result;
  if (CLOUD_SATIN) {
    result = await cloudStitchJob(SATIN_API, endpoint, payload, signal, onProgress);
  } else {
  const response = await fetch(`${SATIN_API}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok)
    throw new Error(
      response.status === 429
        ? "Another Trace job is running. Try again when it finishes."
        : "The local design agent is unavailable."
    );
  const reader = response.body.getReader(),
    decoder = new TextDecoder();
  let pending = "";
  const deliver = (line) => {
    if (!line.trim()) return;
    const data = JSON.parse(line);
    if (data.error) throw new Error(data.error);
    if (data.progress) onProgress(data);
    if (data.result) result = data.result;
  };
  while (!signal.aborted) {
    const { value, done } = await reader.read();
    pending += decoder.decode(value || new Uint8Array(), { stream: !done });
    const lines = pending.split("\n");
    pending = lines.pop();
    lines.forEach(deliver);
    if (done) {
      deliver(pending);
      break;
    }
  }
  if (!result)
    throw new Error("The agent disconnected before returning a result.");
  }
  if (endpoint === "orchestrate")
    for (const layer of result.layers) {
      if (signal.aborted) throw new DOMException("Cancelled", "AbortError");
      if (layer.status === "ready" && layer.choice === "satin") {
        onProgress({
          progress: `Checking app rail pairs for layer ${layer.index + 1}…`,
        });
        await new Promise((resolve) => setTimeout(resolve, 32));
        try {
          layer.result = materializeVideoRails(layer.result);
        } catch (e) {
          layer.status = "failed";
          layer.error = e.message;
          delete layer.result;
        }
      }
    }
  return result;
}

const FILL_KEYS = [
  "angle",
  "row_spacing_mm",
  "max_stitch_length_mm",
  "staggers",
  "expand_mm",
  "fill_underlay",
  "fill_underlay_inset_mm",
  "fill_underlay_row_spacing_mm",
  "fill_underlay_angle",
  "pull_compensation_mm",
  "randomize_stitch_length",
];
export function designAgentUpdate(
  settings,
  rails,
  results,
  analysis,
  satinDefaults
) {
  const nextSettings = { ...settings },
    nextRails = { ...rails };
  for (const layer of results) {
    if (layer.status !== "ready" || layer.choice === "keep") continue;
    const entry = settings[layer.index] || {},
      active = entry.activePassIndex || 0;
    const passes = entry.passes?.length
      ? entry.passes
      : [{ id: "pass_0", ...entry }];
    const existing = passes[active];
    if (!existing)
      throw new Error("Layer pass changed. Review the design again.");
    let patch;
    if (layer.choice === "satin") {
      if (!layer.result?.pairs?.length)
        throw new Error("Missing validated satin rails.");
      patch = { ...satinDefaults, stitchType: "satin", fill_method: undefined };
      nextRails[layer.index] = {
        pairs: layer.result.pairs,
        autoCuts: layer.result.cuts,
        autoSatinBeta: true,
      };
    } else {
      const ai =
        analysis.layers?.find((l) => l.index === layer.index) ||
        analysis.layers?.[layer.index];
      if (!ai)
        throw new Error(
          `Fill optimization is missing for layer ${layer.index + 1}. Run the existing optimizer first.`
        );
      const alreadyFill =
        existing.stitchType &&
        !["satin", "running"].includes(existing.stitchType);
      const optimized = Object.fromEntries(
        FILL_KEYS.filter((k) => ai[k] != null).map((k) => [k, ai[k]])
      );
      // Existing fill parameters are authoritative. For a type conversion use
      // the existing optimizer's parameters, never values invented by Astra.
      patch = alreadyFill
        ? {}
        : { ...optimized, stitchType: "auto_fill", fill_method: "auto_fill" };
      delete nextRails[layer.index];
    }
    nextSettings[layer.index] = {
      ...entry,
      activePassIndex: active,
      passes: passes.map((p, i) => (i === active ? { ...p, ...patch } : p)),
    };
  }
  return { settings: nextSettings, rails: nextRails };
}
